import {
  data as dataResponse,
  isRouteErrorResponse,
  Link,
  Links,
  type LinksFunction,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
} from 'react-router';
import { Notification, TransparentAndeLogo } from '@dinewise/components';
import { captureClientException } from '@dinewise/logger/client';
import { isbot } from 'isbot';
import tailwindStyles from '~/styles/tailwind.css?url';
import { getAppLoadContext } from '~/utils/app-load-context.server.ts';
import { webBaseUrl } from '~/utils/constants.server.ts';
import { getErrorMessage } from '~/utils/errors.ts';
import { commitSessionResponseInit } from '~/utils/sessions.server.ts';
import type { Route } from './+types/root.ts';
import { RedBox } from './components/red-box.tsx';

type FaviconConfig = {
  appleTouchIcon: string;
  png16: string;
  png32: string;
  png96: string;
  ico: string;
  safariMask: string;
  browserConfig: string;
  manifest: string;
  msTileColor: string;
};

const faviconConfig: FaviconConfig = {
  appleTouchIcon: '/favicons/mint/apple-touch-icon-mint-180x180.png',
  png16: '/favicons/mint/favicon-mint-16x16.png',
  png32: '/favicons/mint/favicon-mint-32x32.png',
  png96: '/favicons/mint/favicon-mint-96x96.png',
  ico: '/favicons/mint/favicon-mint.ico',
  safariMask: '/favicons/mint/safari-pinned-tab-mint.svg',
  browserConfig: '/favicons/mint/browserconfig-mint.xml',
  manifest: '/favicons/mint/site-mint.webmanifest',
  msTileColor: '#85FE9D',
};

export const meta: MetaFunction = () => [{ title: 'Ande Payments' }];

export const links: LinksFunction = () => [
  { rel: 'apple-touch-icon', sizes: '180x180', href: faviconConfig.appleTouchIcon },
  { rel: 'icon', type: 'image/png', sizes: '32x32', href: faviconConfig.png32 },
  { rel: 'icon', type: 'image/png', sizes: '16x16', href: faviconConfig.png16 },
  { rel: 'icon', type: 'image/png', sizes: '96x96', href: faviconConfig.png96 },
  { rel: 'icon', type: 'image/x-icon', href: faviconConfig.ico },
  { rel: 'mask-icon', href: faviconConfig.safariMask, color: faviconConfig.msTileColor },
  { rel: 'manifest', href: faviconConfig.manifest },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  { rel: 'stylesheet', href: tailwindStyles },
];

export const loader = async ({ request, context }: Route.LoaderArgs) => {
  const { cspNonce, session } = getAppLoadContext(context);
  // Match ServerRouter / renderToPipeableStream nonce handling (undefined → "") so
  // client <Links nonce> hydrates the same attributes as SSR.
  const nonce = cspNonce ?? '';
  const isBot = isbot(request.headers.get('user-agent'));

  if (isBot) {
    return { notification: null, nonce, webBaseUrl };
  }

  const notification = session.get('notification') ?? null;

  return dataResponse(
    { notification, nonce, webBaseUrl },
    await commitSessionResponseInit(session),
  );
};

export type RootLoader = typeof loader;

export const ErrorBoundary = ({ error }: Route.ErrorBoundaryProps) => {
  const errorMessage = getErrorMessage(error, 'An unexpected error occurred');

  if (!isRouteErrorResponse(error) && error instanceof Error) {
    captureClientException({
      error,
      message: 'Unexpected error occurred in root: ' + errorMessage,
    });
  }

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="msapplication-TileColor" content={faviconConfig.msTileColor} />
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <Meta />
        <Links />
      </head>
      <body className="bg-white font-sans antialiased">
        <main className="relative">
          {error instanceof Error && import.meta.env.DEV ? <RedBox {...{ error }} /> : null}
          <div className="flex h-screen w-screen flex-col items-center bg-white p-8">
            <div className="pb-20">
              <TransparentAndeLogo color="black" className="mx-auto size-10" />
            </div>
            <div className="flex max-w-96 flex-col items-center justify-center">
              <h1 className="pb-7 font-bold text-4xl text-ande-black">Oops!</h1>
              <p className="pb-6 text-center text-ande-gray-500">
                An error occurred. Please refresh the page.
              </p>
              <Link to="/" className="text-ande-black underline">
                Return to Login
              </Link>
            </div>
          </div>
        </main>
        <Scripts />
      </body>
    </html>
  );
};

const Root = ({ loaderData }: Route.ComponentProps) => {
  const { notification, nonce } = loaderData;

  return (
    <html className="h-full" lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="msapplication-TileColor" content={faviconConfig.msTileColor} />
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <Meta />
        <Links nonce={nonce} />
      </head>
      <body className="h-full font-sans antialiased">
        <Outlet />
        <Notification {...{ notification }} />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
};

export default Root;
